<template>
  <RevForm :initial-values :validate @submit="handleSubmit">
    <template #default="{ values, errors }">
      <div class="space-y-16">
        <div class="flex flex-wrap gap-16">
          <RevInputSelect
            id="return-address-selector"
            v-model="values.addressId"
            class="w-full"
            :error="errors.addressId"
            :label="i18n(translations.addresses)"
            :options="returnAddressesOptions"
          />

          <div class="flex w-full flex-wrap gap-16 md:flex-nowrap">
            <RevInputSelect
              id="shipping-provider-selector"
              v-model="values.shippingProviderName"
              class="w-full md:w-1/2"
              :error="errors.shippingProviderName"
              :label="i18n(translations.carrier)"
              :options="shippingProvidersNames"
            />

            <RevInputText
              id="return-label-flow-tracking-number"
              v-model="values.trackingNumber"
              class="w-full md:w-1/2"
              :error="errors.trackingNumber"
              :label="i18n(translations.trackingNumber)"
            />
          </div>
        </div>

        <div class="flex items-center gap-16">
          <NextTaskCheckbox
            v-if="hasNextTask && FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION"
            v-model="values.isNextTaskChecked"
          />
          <RevButton
            :disabled="isLoading"
            :loading="isLoading"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.submit) }}
          </RevButton>
        </div>
      </div>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { useRuntimeConfig } from '#imports'
import { computed, reactive, ref } from 'vue'

import { HttpApiError, shippersAPI } from '@backmarket/http-api'
import type { Address as StandardAddress } from '@backmarket/nuxt-module-address/address'
import { useFormatAddress } from '@backmarket/nuxt-module-address/useFormatAddress'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'

import type { SellerAddress } from '~/api/care-folders/types/seller-informations'
import type { OrderlineOpenTask } from '~/api/seller-after-sales/types'
import { fetchAndGoToNextTask } from '~/scopes/customer-care/components/request.helpers'
import { useOrderlinePublicIdFromRoute } from '~/scopes/customer-care/pages/request/composables/useOrderlinePublicIdFromRoute'

import NextTaskCheckbox from '../../../NextTaskCheckbox/NextTaskCheckbox.vue'

import translations from './ShipBackForm.translations'

const isLoading = ref(false)
const { openErrorToast } = useTheToast()

const { FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION } = useRuntimeConfig().public

const props = defineProps<{
  returnAddresses: SellerAddress[]
  shippingProviders: shippersAPI.GetShippers.Shipper[]
  mostUrgentTask?: OrderlineOpenTask
}>()

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const emit = defineEmits(['done', 'submit'])
const hasNextTask = computed(() => Boolean(props.mostUrgentTask))
const orderlinePublicId = useOrderlinePublicIdFromRoute()

type Values = {
  trackingNumber: string
  shippingProviderName: string
  addressId: string
  isNextTaskChecked: boolean
}

const initialValues = reactive<Values>({
  trackingNumber: '',
  shippingProviderName: '',
  addressId: '',
  isNextTaskChecked: hasNextTask.value,
})

function formatAddress(address: Partial<SellerAddress>): string {
  return useFormatAddress(address as StandardAddress, {
    includeCompany: false,
  }).join(' - ')
}

const returnAddressesOptions = computed(() => {
  return props.returnAddresses.map((address) => ({
    label: `${address.label} - ${formatAddress(address)}`,
    value: address.returnAddressPublicId.toString(),
  }))
})

const shippingProvidersNames = computed(() =>
  // eslint-disable-next-line camelcase
  props.shippingProviders.map(({ shipper_name }) => shipper_name),
)

const validate = makeValidate<Values>({
  trackingNumber: [required(i18n(translations.requiredMessageError))],
  shippingProviderName: [required(i18n(translations.requiredMessageError))],
  addressId: [required(i18n(translations.requiredMessageError))],
})

// async function checkShipmentStatus(shipmentPublicId: string) {
//   const { state } = await $httpFetch(
//     '/service-request/v1/shipments/:publicId/status',
//     {
//       pathParams: { publicId: shipmentPublicId },
//     },
//   )
//   if (state === 'PENDING') {
//     // We throw an error to continue the polling
//     throw new Error('Shipment label creation still pending')
//   }

//   return state
// }

async function handleSubmit(values: Values) {
  isLoading.value = true

  try {
    const selectedShippingProvider = props.shippingProviders.find(
      (provider) => provider.shipper_name === values.shippingProviderName,
    )
    if (!selectedShippingProvider) throw new Error()

    // const { publicId: shipmentPublicId } = await $httpFetch(
    await $httpFetch(
      '/seller-after-sales/api/v1/orderlines/:orderlinePublicId/manual_return',
      {
        pathParams: { orderlinePublicId: orderlinePublicId.value },
        body: {
          carrierCodename: selectedShippingProvider.codename,
          orderlineId: orderlinePublicId.value,
          trackingNumber: values.trackingNumber,
          sellerAddressPublicId: values.addressId,
        },
        method: 'POST',
      },
    )

    // As the process in async, we poll until the state of the shipment is 'CREATED' to terminate the flow
    // TODO: SCARE-2142 to rework the status check function in Care2.0
    // const shipmentStatus = await poll(
    //   () => checkShipmentStatus(shipmentPublicId),
    //   {
    //     retries: CHECK_SHIPMENT_STATUS_RETRIES,
    //     interval: CHECK_SHIPMENT_STATUS_INTERVAL,
    //   },
    // )
    //
    // if (shipmentStatus === 'FAILED') {
    //   throw new Error(
    //     `Shipment label creation failed (shipmentPublicId: ${shipmentPublicId})`,
    //   )
    // }

    if (values.isNextTaskChecked && FF_ENABLE_CUSTOMER_CARE_REQUEST_NAVIGATION)
      fetchAndGoToNextTask(props.mostUrgentTask?.id).catch(() => {
        openErrorToast()
      })

    emit('done')
  } catch (err) {
    openErrorToast()

    const logger = useLogger()
    const error = err as HttpApiError
    logger.error(
      `An error occurred while creating a seller return for help request with orderline #${orderlinePublicId.value}`,
      {
        error,
        owners: ['bot-squad-seller-care-front'],
      },
    )
  } finally {
    isLoading.value = false
  }
}
</script>
